import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";

const links = [
  { to: "/", label: "Home" },
  { to: "/projects", label: "Projects" },
  { to: "/track-record", label: "Track Record" },
  { to: "/about", label: "About" },
  { to: "/careers", label: "Careers" },
  { to: "/contact", label: "Contact" },
];

export const Logo = ({ light = false }) => (
  <Link to="/" data-testid="logo-link" aria-label="Parsn Group — home" className="flex items-center group">
    {light ? (
      <span className="bg-white px-4 py-2.5 inline-flex">
        <img src="/assets/brand/parsn-logo.webp" alt="Parsn Group" className="h-7 w-auto" />
      </span>
    ) : (
      <img
        src="/assets/brand/parsn-logo-transparent.png"
        alt="Parsn Group"
        className="h-8 sm:h-9 w-auto transition-transform duration-300 group-hover:scale-[1.03]"
      />
    )}
  </Link>
);

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 border-b transition-[background-color,box-shadow,border-color] duration-500 ${
        scrolled ? "bg-white/85 backdrop-blur-xl border-gray-200 shadow-ambient" : "bg-white/60 backdrop-blur-md border-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 h-16 sm:h-20 flex items-center justify-between">
        <Logo />
        <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-sm font-medium tracking-wide transition-colors duration-300 hover:text-navy ${
                  isActive ? "text-navy" : "text-gray-500"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/contact"
            data-testid="nav-enquire-button"
            className="text-sm font-semibold px-5 py-2.5 bg-brand text-white transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-[#e0671a]"
          >
            Enquire
          </Link>
        </nav>
        <button
          data-testid="mobile-menu-toggle"
          aria-label={open ? "Close menu" : "Open menu"}
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden p-2 text-navy"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-white border-t border-gray-200"
            aria-label="Mobile"
          >
            <div className="px-5 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `font-serif text-2xl ${isActive ? "text-navy" : "text-gray-500"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                data-testid="mobile-nav-enquire-button"
                className="mt-2 text-center text-sm font-semibold px-5 py-3 bg-brand text-white"
              >
                Enquire
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
